/**
 * Recompte d'existències (OBR-147): les formes de les files que la base de dades desa (`stock_counts`, `stock_count_lines`), les
 * entrades de cada RPC i els arguments exactes de les RPC (`p_…`).
 *
 * El servidor és l'autoritat de cada regla (`create_stock_count`, `save_stock_count_lines`, `add_stock_count_line`,
 * `validate_stock_count`, `cancel_stock_count`); aquestes formes diuen el mateix ABANS d'enviar res i, a la lectura, que el que torna
 * PostgREST és el que la pantalla espera. Les quantitats són en UNITAT BASE de l'article.
 */

import { z } from 'zod';
import {
  STOCK_COUNT_SAVE_BATCH_LIMIT,
  STOCK_COUNT_SCOPE_TYPES,
  STOCK_COUNT_STATUSES,
} from '../domain/stockCount.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  dateString,
  quantity,
  nonNegativeQuantity,
} from './common.js';

/** El text de nota: curt (el servidor el limita a 500 caràcters). */
const shortNote = nonEmptyString.max(500);

// ── Les files ───────────────────────────────────────────────────────────────────────

/** El resum que torna `validate_stock_count` (i que es desa a la capçalera). */
export const StockCountSummary = z
  .object({
    count_id: uuid,
    count_number: nonEmptyString,
    lines: z.number().int().nonnegative(),
    counted: z.number().int().nonnegative(),
    not_counted: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    adjusted: z.number().int().nonnegative(),
    increases: z.number().int().nonnegative(),
    decreases: z.number().int().nonnegative(),
    lots_created: z.number().int().nonnegative(),
    changed_since_snapshot: z.number().int().nonnegative(),
  })
  .strict();
export type StockCountSummary = z.infer<typeof StockCountSummary>;

/** La capçalera d'un recompte, tal com la desa `stock_counts`. */
export const StockCount = z
  .object({
    id: uuid,
    tenant_id: uuid,
    count_number: nonEmptyString,
    scope_type: z.enum(STOCK_COUNT_SCOPE_TYPES),
    scope_zone_id: uuid.nullable(),
    scope_section_id: uuid.nullable(),
    scope_location_id: uuid.nullable(),
    scope_article_id: uuid.nullable(),
    status: z.enum(STOCK_COUNT_STATUSES),
    note: z.string().nullable(),
    line_count: z.number().int().nonnegative(),
    counted_count: z.number().int().nonnegative(),
    adjusted_count: z.number().int().nonnegative().nullable(),
    client_uuid: uuid.nullable(),
    created_by: uuid,
    created_at: timestamptz,
    validated_by: uuid.nullable(),
    validated_at: timestamptz.nullable(),
    validation_client_uuid: uuid.nullable(),
    validation_summary: StockCountSummary.nullable(),
    cancelled_by: uuid.nullable(),
    cancelled_at: timestamptz.nullable(),
    cancel_reason: z.string().nullable(),
  })
  .strict();
export type StockCount = z.infer<typeof StockCount>;

/** Una línia d'un recompte, tal com la desa `stock_count_lines`. */
export const StockCountLine = z
  .object({
    id: uuid,
    tenant_id: uuid,
    stock_count_id: uuid,
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    theoretical_quantity: quantity,
    counted_quantity: nonNegativeQuantity.nullable(),
    found_unlisted: z.boolean(),
    is_new_lot: z.boolean(),
    new_lot_code: z.string().nullable(),
    expiry_date: dateString.nullable(),
    note: z.string().nullable(),
    current_quantity: quantity.nullable(),
    adjustment_quantity: quantity.nullable(),
    movement_id: uuid.nullable(),
    created_supplier_lot_id: uuid.nullable(),
    client_uuid: uuid.nullable(),
    /** Els `client_uuid` d'«Afegeix un lot trobat» ja aplicats sobre la línia (idempotència de la suma; els últims 20). */
    applied_uuids: z.array(uuid),
    created_by: uuid.nullable(),
    created_at: timestamptz,
    updated_by: uuid.nullable(),
    updated_at: timestamptz,
  })
  .strict();
export type StockCountLine = z.infer<typeof StockCountLine>;

// ── Entrades de les RPC ──────────────────────────────────────────────────────────────

/**
 * Crear un recompte (RPC `create_stock_count`): un abast i, si cal, el seu identificador (exactament el del tipus). `client_uuid`
 * fa el reintent idempotent: s'ha de generar UN cop per obertura del diàleg i reutilitzar en cada reintent.
 */
export const StockCountCreateInput = z
  .object({
    scope_type: z.enum(STOCK_COUNT_SCOPE_TYPES),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    article_id: uuid.nullable(),
    note: shortNote.nullable().optional(),
    client_uuid: uuid.nullable().optional(),
  })
  .strict()
  .refine(
    (m) => {
      const ids = {
        zone: m.zone_id,
        section: m.section_id,
        location: m.location_id,
        article: m.article_id,
      } as const;
      return (Object.keys(ids) as Array<keyof typeof ids>).every((kind) =>
        m.scope_type === kind ? ids[kind] !== null : ids[kind] === null,
      );
    },
    { message: 'the scope carries exactly the identifier of its type', path: ['scope_type'] },
  );
export type StockCountCreateInput = z.infer<typeof StockCountCreateInput>;

/** Els arguments de l'RPC `create_stock_count`, amb els noms exactes de la funció (`p_…`). */
export function createStockCountRpcArgs(input: StockCountCreateInput): Record<string, unknown> {
  return {
    p_scope_type: input.scope_type,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_article_id: input.article_id,
    p_note: input.note ?? null,
    p_client_uuid: input.client_uuid ?? null,
  };
}

/**
 * Un element del bloc de `save_stock_count_lines`. Cada camp és OPCIONAL i «absent» vol dir «no es toca»; `null` vol dir «buida-ho»
 * (una línia torna a «no comptada»). `confirm_theoretical` posa la teòrica de la FOTO com a comptada (el servidor la llegeix, no es
 * transmet; el BackOffice no l'usa i envia la quantitat explícita del saldo d'ara: vegeu `confirmedQuantity`). `remove` només val per
 * a una línia afegida a mà.
 */
export const StockCountLineSaveItem = z
  .object({
    id: uuid,
    counted_quantity: nonNegativeQuantity.max(99_999_999_999).nullable().optional(),
    confirm_theoretical: z.boolean().optional(),
    note: shortNote.nullable().optional(),
    expiry_date: dateString.nullable().optional(),
    remove: z.boolean().optional(),
  })
  .strict();
export type StockCountLineSaveItem = z.infer<typeof StockCountLineSaveItem>;

/** El bloc de línies que es desa d'un cop (mai una crida per línia). */
export const StockCountSaveLinesInput = z
  .object({
    count_id: uuid,
    lines: z.array(StockCountLineSaveItem).max(STOCK_COUNT_SAVE_BATCH_LIMIT),
  })
  .strict();
export type StockCountSaveLinesInput = z.infer<typeof StockCountSaveLinesInput>;

/** Els arguments de l'RPC `save_stock_count_lines`. Els camps absents d'un element no viatgen (el JSON els distingeix de `null`). */
export function saveStockCountLinesRpcArgs(
  input: StockCountSaveLinesInput,
): Record<string, unknown> {
  return { p_count_id: input.count_id, p_lines: input.lines.map((line) => ({ ...line })) };
}

/**
 * Afegir una línia trobada (RPC `add_stock_count_line`): un lot existent en un lloc, o un lot nou. Si la coordenada (lot + lloc) ja
 * té línia, `add_to_existing` tria què passa amb la quantitat: `true` la SUMA a la ja comptada, `false` (per defecte) la SUBSTITUEIX.
 * Les dues maneres són idempotents amb `client_uuid`.
 */
export const StockCountAddLineInput = z
  .object({
    count_id: uuid,
    article_id: uuid,
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    counted_quantity: quantity,
    new_lot: z.boolean(),
    new_lot_code: nonEmptyString.max(120).nullable().optional(),
    expiry_date: dateString.nullable().optional(),
    note: shortNote.nullable().optional(),
    client_uuid: uuid.nullable().optional(),
    add_to_existing: z.boolean().optional(),
  })
  .strict()
  .refine((m) => !(m.supplier_lot_id !== null && m.finished_lot_id !== null), {
    message: 'a line belongs to one lot at most',
    path: ['finished_lot_id'],
  })
  .refine((m) => (m.new_lot ? m.counted_quantity > 0 : m.counted_quantity >= 0), {
    message: 'a new lot needs a positive quantity; an existing one, zero or more',
    path: ['counted_quantity'],
  })
  .refine((m) => (m.new_lot ? m.supplier_lot_id === null && m.finished_lot_id === null : true), {
    message: 'a new lot has no existing lot',
    path: ['supplier_lot_id'],
  })
  .refine((m) => m.new_lot || m.supplier_lot_id !== null || m.finished_lot_id !== null, {
    message: 'a found line needs a lot (an existing one or a new one)',
    path: ['supplier_lot_id'],
  })
  .refine((m) => m.new_lot || (m.new_lot_code ?? null) === null, {
    message: 'a supplier lot code only goes with a new lot',
    path: ['new_lot_code'],
  });
export type StockCountAddLineInput = z.infer<typeof StockCountAddLineInput>;

/** Els arguments de l'RPC `add_stock_count_line`, amb els noms exactes de la funció (`p_…`). */
export function addStockCountLineRpcArgs(input: StockCountAddLineInput): Record<string, unknown> {
  return {
    p_count_id: input.count_id,
    p_article_id: input.article_id,
    p_supplier_lot_id: input.supplier_lot_id,
    p_finished_lot_id: input.finished_lot_id,
    p_zone_id: input.zone_id,
    p_section_id: input.section_id,
    p_location_id: input.location_id,
    p_counted_quantity: input.counted_quantity,
    p_new_lot: input.new_lot,
    p_new_lot_code: input.new_lot_code ?? null,
    p_expiry_date: input.expiry_date ?? null,
    p_note: input.note ?? null,
    p_client_uuid: input.client_uuid ?? null,
    p_add_to_existing: input.add_to_existing ?? false,
  };
}

/** Validar un recompte (RPC `validate_stock_count`): `client_uuid` fa el reintent idempotent (el mateix uuid torna el mateix resum). */
export const StockCountValidateInput = z
  .object({ count_id: uuid, client_uuid: uuid.nullable().optional() })
  .strict();
export type StockCountValidateInput = z.infer<typeof StockCountValidateInput>;

export function validateStockCountRpcArgs(input: StockCountValidateInput): Record<string, unknown> {
  return { p_count_id: input.count_id, p_client_uuid: input.client_uuid ?? null };
}

/** Cancel·lar un recompte (RPC `cancel_stock_count`): el motiu escrit és obligatori. */
export const StockCountCancelInput = z
  .object({ count_id: uuid, reason: nonEmptyString.max(500) })
  .strict();
export type StockCountCancelInput = z.infer<typeof StockCountCancelInput>;

export function cancelStockCountRpcArgs(input: StockCountCancelInput): Record<string, unknown> {
  return { p_count_id: input.count_id, p_reason: input.reason };
}

/** La teòrica d'ara d'una línia (RPC `stock_count_current_quantities`). */
export const StockCountCurrentQuantity = z
  .object({ line_id: uuid, current_quantity: quantity })
  .strict();
export type StockCountCurrentQuantity = z.infer<typeof StockCountCurrentQuantity>;

// ── Les lectures de l'App Obrador (OBR-149) ─────────────────────────────────────────────

/** Un numèric que PostgREST pot tornar com a número o com a text («11.500»): l'App el llegeix amb `Number`. */
const numericOrText = z.union([z.number(), z.string()]);

/**
 * Una fila de `stock_counts_for_app` (els recomptes oberts de l'obrador i l'estat dels que l'aparell ja coneix): la capçalera, el text de
 * l'abast ja resolt i els identificadors de l'abast (per jutjar «dins de l'abast» a l'aparell). Tot `.strict()`: una columna que no coneixem
 * és un error, no un valor que es perd en silenci.
 */
export const StockCountAppRow = z
  .object({
    id: uuid,
    count_number: nonEmptyString,
    status: z.enum(STOCK_COUNT_STATUSES),
    scope_type: z.enum(STOCK_COUNT_SCOPE_TYPES),
    scope_text: z.string().nullable(),
    scope_zone_id: uuid.nullable(),
    scope_section_id: uuid.nullable(),
    scope_location_id: uuid.nullable(),
    scope_article_id: uuid.nullable(),
    note: z.string().nullable(),
    line_count: z.number().int().nonnegative(),
    counted_count: z.number().int().nonnegative(),
    created_at: timestamptz,
    /** Quan es va validar o cancel·lar; null mentre és obert. */
    closed_at: timestamptz.nullable(),
    /** Quants recomptes oberts té l'obrador EN TOTAL (el mateix a totes les files): la llista n'hi porta els més nous. */
    open_total: z.number().int().nonnegative(),
  })
  .strict();
export type StockCountAppRow = z.infer<typeof StockCountAppRow>;

/**
 * Una fila de `stock_count_lines_for_app` (una línia d'UN recompte amb l'article, la unitat, el lot i el text del lloc ja resolts). Les
 * quantitats poden arribar com a text. `counted_by` és qui va desar la línia per última vegada (només fiable quan `counted_quantity` no és
 * null: vegeu la migració 20261106100000).
 */
export const StockCountAppLineRow = z
  .object({
    line_id: uuid,
    article_id: uuid,
    article_name: z.string(),
    article_sku: z.string(),
    unit_code: z.string().nullable(),
    supplier_lot_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    lot_number: z.string().nullable(),
    lot_expiry_date: dateString.nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    place_text: z.string().nullable(),
    theoretical_quantity: numericOrText,
    counted_quantity: numericOrText.nullable(),
    counted_by: uuid.nullable(),
    updated_at: timestamptz,
    found_unlisted: z.boolean(),
    is_new_lot: z.boolean(),
    new_lot_code: z.string().nullable(),
    expiry_date: dateString.nullable(),
    note: z.string().nullable(),
  })
  .strict();
export type StockCountAppLineRow = z.infer<typeof StockCountAppLineRow>;
